/**
 * Keystroke handoff between the search input generations (PR #1042 blocker 4).
 *
 * Since COM-189 wave 4 the CIO autocomplete widget is a lazy chunk, so on a
 * cold cache there are up to three inputs in a search interaction's lifetime:
 *
 *   1. the header's hidden iOS keyboard warm-up input (focused in the tap
 *      gesture — see hec-header.tsx),
 *   2. the PlainSearchFallback's real `<input>` (renders while the widget
 *      chunk streams),
 *   3. the widget's own input (downshift-managed).
 *
 * Characters typed into 1 or 2 during the chunk window used to be DISCARDED
 * on each handoff — and when the chunk failed outright, focus stayed on the
 * invisible warm-up input for the session, silently swallowing every
 * keystroke. This module carries the buffered value (and the "user was
 * typing" signal) across the handoffs:
 *
 *  - Only inputs explicitly marked with `PENDING_SEARCH_ATTR` are ever
 *    adopted from, so focus/text is never stolen from unrelated inputs
 *    (email signup, checkout fields) that happen to be focused while a
 *    chunk loads.
 *  - The Suspense fallback→widget swap unmounts the fallback BEFORE the
 *    widget's effects run (activeElement is already gone), so the fallback
 *    stashes its value on unmount and the widget consumes the stash.
 */
export const PENDING_SEARCH_ATTR = "data-search-pending";

let stashedValue = "";

/**
 * Called by a pending-source input when it unmounts while holding buffered
 * keystrokes (the fallback→widget swap). Consumed exactly once by the next
 * `adoptPendingSearch()`.
 */
export function stashPendingSearchValue(value: string): void {
  stashedValue = value;
}

/**
 * Adopt buffered keystrokes + the keyboard/focus signal from the currently
 * focused pending-source input (clearing it), or from the unmount stash.
 * Returns `{ value: "", hadFocus: false }` when there is nothing to adopt.
 */
export function adoptPendingSearch(): { value: string; hadFocus: boolean } {
  let value = "";
  let hadFocus = false;
  if (typeof document !== "undefined") {
    const active = document.activeElement;
    if (
      active instanceof HTMLInputElement &&
      active.hasAttribute(PENDING_SEARCH_ATTR)
    ) {
      value = active.value;
      active.value = "";
      hadFocus = true;
    }
  }
  if (!value && stashedValue) {
    // The stashing input was the live search surface when it unmounted —
    // restoring focus to its successor continues the interaction.
    value = stashedValue;
    hadFocus = true;
  }
  stashedValue = "";
  return { value, hadFocus };
}
